import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@codes/ui-kit/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Checkbox } from '@codes/ui-kit/components/ui/checkbox';
import { AlertTriangle, Download, Lock } from 'lucide-react';

import { CopyButton } from './CopyButton';
import { downloadApiKeyFile } from '../utils';

interface OpenApiKeySuccessDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    apiKey: string;
    keyName: string;
}

export const OpenApiKeySuccessDialog = ({ open, onOpenChange, apiKey, keyName }: OpenApiKeySuccessDialogProps) => {
    const { t } = useTranslation();
    const [hasConfirmed, setHasConfirmed] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleClose = () => {
        setHasConfirmed(false);
        setCopied(false);
        onOpenChange(false);
    };

    const handleDownload = () => {
        downloadApiKeyFile(keyName, apiKey);
    };

    const handleCopySuccess = () => {
        setCopied(true);
    };

    return (
        <AlertDialog open={open} onOpenChange={handleClose}>
            <AlertDialogContent className="sm:max-w-xl">
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5 text-green-600" />
                        {t('openApiKeys.successDialog.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>{t('openApiKeys.successDialog.description')}</AlertDialogDescription>
                </AlertDialogHeader>

                <Alert variant="warning" className="my-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t('openApiKeys.successDialog.warningTitle')}</AlertTitle>
                    <AlertDescription>{t('openApiKeys.successDialog.warning')}</AlertDescription>
                </Alert>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-muted-foreground">{t('openApiKeys.apiKey')}</label>
                        <div className="relative">
                            <code className="block p-4 pr-12 bg-slate-950 text-green-400 rounded-lg font-mono text-sm break-all border border-slate-800">
                                {apiKey}
                            </code>
                            <div className="absolute top-2 right-2">
                                <CopyButton value={apiKey} onCopySuccess={handleCopySuccess} />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <Button onClick={handleCopySuccess} variant="default" className="flex-1" disabled={copied}>
                            {copied ? t('openApiKeys.copied') : t('openApiKeys.copyKey')}
                        </Button>
                        <Button onClick={handleDownload} variant="outline" className="flex-1">
                            <Download className="h-4 w-4 mr-2" />
                            {t('openApiKeys.download')}
                        </Button>
                    </div>

                    <div className="flex items-start gap-2 pt-4 border-t">
                        <Checkbox
                            id="confirm-saved"
                            checked={hasConfirmed}
                            onCheckedChange={checked => setHasConfirmed(checked === true)}
                        />
                        <label htmlFor="confirm-saved" className="text-sm leading-tight cursor-pointer">
                            {t('openApiKeys.successDialog.confirmSaved')}
                        </label>
                    </div>
                </div>

                <AlertDialogFooter>
                    <AlertDialogAction onClick={handleClose} disabled={!hasConfirmed && !copied}>
                        {hasConfirmed || copied
                            ? t('openApiKeys.successDialog.done')
                            : t('openApiKeys.successDialog.close')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
